import { useState } from "react";
import { createPortal } from "react-dom";
import { Menu, X } from "lucide-react";

// Height that respects the visible viewport (keyboard-aware) via the global --app-vh var.
const sheetMaxHeight = { maxHeight: "calc(var(--app-vh, 100vh) * 0.9)" };

/**
 * Bottom-sheet / centered modal used on phones. Docks to the bottom of the screen on
 * small viewports and centers on larger ones. Never draggable — always reachable.
 */
export const MobileSheet = ({ open, onClose, title, children, testid = "mobile-sheet" }) => {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[9998] bg-black/60 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid={`${testid}-backdrop`}
    >
      <div
        className="w-full sm:w-auto sm:min-w-[360px] sm:max-w-md flex flex-col retro-window overflow-hidden animate-in slide-in-from-bottom duration-200"
        style={sheetMaxHeight}
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
      >
        {title && (
          <div className="retro-title-bar py-1 flex items-center justify-between flex-shrink-0">
            <span className="text-xs">{title}</span>
            <button
              className="close-button-visible"
              onClick={onClose}
              data-testid={`${testid}-close`}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
};

/**
 * Hamburger button + action bottom-sheet. `items` is an array of
 * { label, icon, onClick, hidden?, badge?, className? }.
 */
export const MobileMenu = ({ items = [], label = "Menu", testid = "mobile-menu", buttonClassName = "" }) => {
  const [open, setOpen] = useState(false);
  const visible = items.filter((it) => it && !it.hidden);
  return (
    <>
      <button
        className={`retro-button flex items-center gap-1 text-xs px-2 py-0.5 font-semibold ${buttonClassName}`}
        onClick={() => setOpen(true)}
        data-testid={`${testid}-btn`}
        aria-label={label}
        title={label}
      >
        <Menu className="w-4 h-4" />
        {label}
      </button>
      <MobileSheet open={open} onClose={() => setOpen(false)} title={label} testid={`${testid}-sheet`}>
        <div className="grid grid-cols-2 gap-2 p-3">
          {visible.map((it, i) => (
            <button
              key={i}
              className={`retro-button relative flex items-center gap-2 text-sm px-3 py-3 justify-start ${it.className || ""}`}
              onClick={() => {
                setOpen(false);
                it.onClick?.();
              }}
              data-testid={it.testid || `${testid}-item-${i}`}
            >
              {it.icon}
              <span className="truncate">{it.label}</span>
              {it.badge > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border border-white">
                  {it.badge > 99 ? "99+" : it.badge}
                </span>
              )}
              {it.dot && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
              )}
            </button>
          ))}
        </div>
      </MobileSheet>
    </>
  );
};

export default MobileMenu;
